/**
 * useDonationQueries — 건전지 개수의 단일 출처.
 *
 * 종전에는 `data/donation.ts` 의 `getBatteryCount()` 가 sessionStorage 를 읽었다.
 * 그건 결제가 없던 시절의 프로토타입이고, 개수의 진짜 출처는 **서버(계정 기준)** 다 —
 * 소모성 IAP 는 복원이 안 돼서 기기에 두면 앱을 지우는 순간 사라진다(OI-10).
 *
 * 컬렉션과 같은 정책(staleTime 30초 + 포커스 재조회)을 쓴다. 개수는 본인만 바꾸는
 * 값이라 폴링할 이유가 없고, 후원 성공 뒤 무효화하면 충분하다.
 *
 * ⚠ 환불로 개수가 **줄어드는** 경로가 있다(환불 대사 배치가 하루 1회). 그래서
 *   `staleTime` 이 지나면 다시 읽어야 하고, 로컬 캐시를 진실로 삼으면 안 된다.
 *   줄어든 것은 조용히 반영한다 — 알림·토스트를 띄우지 않는다(환불은 본인 행위라
 *   알리면 추궁으로 읽힌다. BRAND §3.7).
 *
 * ⚠ 비로그인은 호출하지 않는다. 표식이 계정 기준이라 붙일 곳이 없어 401 만 받는다.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchBatteryCount,
  fetchDonationEnabled,
  verifyPurchase,
  type BatteryCount,
  type VerifyRequest,
  type VerifyResult,
} from "../api/donation";
import { useAuthStore } from "../store/authStore";

export const BATTERY_KEY = ["donation", "batteries"] as const;
export const DONATION_ENABLED_KEY = ["donation", "enabled"] as const;

/**
 * 후원 진입점을 그려도 되는가 (서버 스위치 + 스토어 자격증명 + 강등의 곱).
 *
 * ⚠ **아직 모르는 동안(로딩·실패)에는 false 다.** 스토어 상품 등록 전에 버튼을 그리면
 *   누르는 순간 실패하는 자리가 되고, 그건 안 보이는 것보다 나쁘다. 비로그인도 부른다 —
 *   표식이 아니라 기능 개폐라 계정과 무관하고, 엔드포인트도 permitAll 이다.
 */
export function useDonationEnabledQuery() {
  return useQuery<boolean>({
    queryKey: DONATION_ENABLED_KEY,
    queryFn: () => fetchDonationEnabled(),
    // 운영에서 어드민이 켜면 다음 앱 실행·포커스에 반영되면 충분하다. 자주 물을 값이 아니다.
    staleTime: 5 * 60_000,
  });
}

/**
 * 내 건전지 수.
 *
 * ⚠ 로딩 중에는 **아무것도 그리지 않는다.** 0 을 먼저 보여 주면 후원자가 자기 표식이
 *   사라졌다 오해하고, '건전지 0개' 같은 부재 표시는 만들지 않기로 했다(수익모델 §4.3).
 *   그래서 화면은 `data` 가 오기 전까지 표식 자리를 비워 둔다.
 */
export function useBatteryCountQuery() {
  const isAuthed = useAuthStore((s) => !!s.accessToken);
  return useQuery<BatteryCount>({
    queryKey: BATTERY_KEY,
    queryFn: () => fetchBatteryCount(),
    enabled: isAuthed,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * 영수증 검증 + 적립.
 *
 * 성공하면 개수를 무효화해 다시 읽는다 — 응답의 `batteryCount` 를 직접 심지 않는 이유는,
 * 같은 계정이 다른 기기에서 후원했을 수 있어 서버가 진실이기 때문이다.
 *
 * ⚠ `duplicated: true` 도 **성공이다.** 서버가 커밋한 직후 응답이 유실되면 앱이 같은
 *   영수증을 다시 보내는데, 그걸 실패로 다루면 돈 낸 사용자가 실패 화면을 본다.
 */
export function useVerifyPurchaseMutation() {
  const queryClient = useQueryClient();
  return useMutation<VerifyResult, unknown, VerifyRequest>({
    mutationFn: (req) => verifyPurchase(req),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BATTERY_KEY });
    },
  });
}
